import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Navigation } from "../components/Navigation";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

/**
 * /p/:short_id — friendly short URL that resolves server-side to the full
 * /paga?... URL. Used so admins can share clean, WhatsApp-friendly links.
 *
 * Flow:
 *   1. customer clicks https://edicolavillaricca.com/p/ab7Xq2K (auto-linked
 *      by WhatsApp/SMS because it's short and has no query string)
 *   2. this component calls GET /api/custom-payment/resolve/{short_id}
 *   3. backend returns {full_query: "i=12.50&c=...&x=...&s=..."}
 *   4. we client-side replace() to /paga?{full_query}
 */
export const CustomPaymentShortLinkPage = () => {
  const { short_id } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const resolve = async () => {
      try {
        const { data } = await axios.get(`${API}/custom-payment/resolve/${short_id}`);
        if (cancelled) return;
        if (data && data.full_query) {
          navigate(`/paga?${data.full_query}`, { replace: true });
        } else {
          setError("notfound");
        }
      } catch (err) {
        if (cancelled) return;
        setError(err.response?.status === 404 ? "notfound" : "error");
      }
    };
    resolve();
    return () => {
      cancelled = true;
    };
  }, [short_id, navigate]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="max-w-2xl mx-auto px-4 py-16">
        <Card>
          <CardContent className="p-8 text-center">
            {!error && (
              <>
                <div className="text-5xl mb-4 animate-pulse">⏳</div>
                <h1 className="text-2xl font-bold mb-2">Apertura del link in corso...</h1>
                <p className="text-gray-600">Un momento.</p>
              </>
            )}
            {error === "notfound" && (
              <>
                <div className="text-5xl mb-4">⚠️</div>
                <h1 className="text-2xl font-bold mb-2">Link non trovato</h1>
                <p className="text-gray-600 mb-6">
                  Questo link non è più disponibile. Chiedi al venditore di rigenerarlo.
                </p>
                <Button onClick={() => navigate("/")} data-testid="paga-short-home-btn">
                  Torna alla home
                </Button>
              </>
            )}
            {error === "error" && (
              <>
                <div className="text-5xl mb-4">⚠️</div>
                <h1 className="text-2xl font-bold mb-2">Errore di connessione</h1>
                <p className="text-gray-600">Riprova tra qualche secondo.</p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

/**
 * /paga?i=12.50&c=Differenza+spedizione[&n=Mario][&e=mario@x.it]
 *
 * Admin-generated payment link. No catalog product is involved, no shipping
 * is computed. The amount is fixed by the URL (read-only for the customer);
 * the customer fills in their contact details and pays via Stripe.
 */
export const CustomPaymentPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const importo = parseFloat(params.get("i") || "0");
  const causale = (params.get("c") || "").trim();
  const prefilledName = params.get("n") || "";
  const prefilledEmail = params.get("e") || "";
  const expiresAt = parseInt(params.get("x") || "0", 10) || null; // UNIX timestamp
  const signature = params.get("s") || null;

  const nowSec = Math.floor(Date.now() / 1000);
  const isExpired = expiresAt && nowSec > expiresAt;
  const expiresLabel = expiresAt
    ? new Date(expiresAt * 1000).toLocaleString("it-IT", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;
  const daysLeft = expiresAt ? Math.max(0, Math.ceil((expiresAt - nowSec) / 86400)) : null;

  const [form, setForm] = useState({
    customer_name: prefilledName,
    customer_email: prefilledEmail,
    customer_phone: "",
    accept: false,
  });
  const [loading, setLoading] = useState(false);

  const validLink = importo > 0 && causale.length > 0;

  const handlePay = async () => {
    if (!form.customer_name.trim() || !form.customer_email.trim()) {
      toast.error("Nome ed email sono obbligatori");
      return;
    }
    if (!form.accept) {
      toast.error("Conferma di aver letto la causale prima di procedere");
      return;
    }
    setLoading(true);
    try {
      const body = {
        amount: importo,
        causale,
        customer_name: form.customer_name.trim(),
        customer_email: form.customer_email.trim(),
        customer_phone: form.customer_phone.trim(),
      };
      if (expiresAt && signature) {
        body.expires_at = expiresAt;
        body.signature = signature;
      }
      const { data } = await axios.post(`${API}/custom-payment/checkout`, body);
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Risposta inattesa dal server");
        setLoading(false);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Errore sconosciuto";
      toast.error(typeof msg === "string" ? msg : "Errore nella creazione del pagamento");
      setLoading(false);
    }
  };

  if (!validLink) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-2xl mx-auto px-4 py-16">
          <Card>
            <CardContent className="p-8 text-center">
              <div className="text-5xl mb-4">⚠️</div>
              <h1 className="text-2xl font-bold mb-2">Link non valido</h1>
              <p className="text-gray-600 mb-6">
                Questo link di pagamento è mancante o malformato. Chiedi al venditore di rigenerarlo.
              </p>
              <Button onClick={() => navigate("/")} data-testid="paga-home-btn">
                Torna alla home
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (isExpired) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-2xl mx-auto px-4 py-16">
          <Card>
            <CardContent className="p-8 text-center">
              <div className="text-5xl mb-4">⌛</div>
              <h1 className="text-2xl font-bold mb-2">Link scaduto</h1>
              <p className="text-gray-600 mb-2">
                Questo link di pagamento è scaduto il <strong>{expiresLabel}</strong>.
              </p>
              <p className="text-gray-600 mb-6">
                Contatta il venditore per riceverne uno nuovo.
              </p>
              <Button onClick={() => navigate("/")} data-testid="paga-expired-home-btn">
                Torna alla home
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="max-w-2xl mx-auto px-4 py-12">
        <Card>
          <CardContent className="p-6 sm:p-8">
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-amber-100 text-amber-800 rounded-full text-sm font-medium mb-3">
                💳 Pagamento Personalizzato
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-1">Edicola Cerasuolo</h1>
              <p className="text-sm text-gray-500">Completa il pagamento richiesto dal venditore</p>
            </div>

            {/* Importo + causale (read-only) */}
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-5 mb-6">
              <div className="text-sm font-medium text-gray-600 mb-1">Importo da pagare</div>
              <div className="text-4xl font-bold text-gray-900 mb-3" data-testid="paga-importo">
                € {importo.toFixed(2).replace(".", ",")}
              </div>
              <div className="text-sm font-medium text-gray-600 mb-0.5">Causale</div>
              <div className="text-base text-gray-800" data-testid="paga-causale">
                {causale}
              </div>
              {expiresAt && (
                <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 bg-amber-100 border border-amber-200 rounded-full text-xs font-medium text-amber-800" data-testid="paga-expiry-badge">
                  ⌛ {daysLeft === 0
                    ? "Scade oggi"
                    : daysLeft === 1
                    ? "Scade domani"
                    : `Scade tra ${daysLeft} giorni`}
                  <span className="text-amber-600">· {expiresLabel}</span>
                </div>
              )}
            </div>

            {/* Customer info */}
            <div className="space-y-4 mb-6">
              <div>
                <Label htmlFor="customer_name">Nome e Cognome *</Label>
                <Input
                  id="customer_name"
                  value={form.customer_name}
                  onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                  placeholder="Mario Rossi"
                  data-testid="paga-name-input"
                />
              </div>
              <div>
                <Label htmlFor="customer_email">Email *</Label>
                <Input
                  id="customer_email"
                  type="email"
                  value={form.customer_email}
                  onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                  placeholder="mario@example.com"
                  data-testid="paga-email-input"
                />
              </div>
              <div>
                <Label htmlFor="customer_phone">Telefono (opzionale)</Label>
                <Input
                  id="customer_phone"
                  type="tel"
                  value={form.customer_phone}
                  onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                  placeholder="+39 ..."
                  data-testid="paga-phone-input"
                />
              </div>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.accept}
                  onChange={(e) => setForm({ ...form, accept: e.target.checked })}
                  className="mt-1"
                  data-testid="paga-accept-checkbox"
                />
                <span className="text-sm text-gray-700">
                  Confermo di aver letto la causale e di voler procedere al pagamento di
                  <strong> € {importo.toFixed(2).replace(".", ",")}</strong>
                </span>
              </label>
            </div>

            <Button
              onClick={handlePay}
              disabled={loading}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold py-6 text-base"
              data-testid="paga-submit-btn"
            >
              {loading ? "Reindirizzamento..." : `💳 Paga € ${importo.toFixed(2).replace(".", ",")} con carta`}
            </Button>

            <p className="text-xs text-center text-gray-500 mt-4">
              Pagamento sicuro tramite Stripe. Nessuna spedizione associata a questo pagamento.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

/**
 * /paga/grazie?session_id=cs_xxx — confirmation page after Stripe checkout.
 * Polls the backend to confirm payment status.
 */
export const CustomPaymentSuccessPage = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("loading");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    const maxAttempts = 10;
    const poll = async () => {
      try {
        const { data: doc } = await axios.get(`${API}/custom-payment/status/${sessionId}`);
        setData(doc);
        if (doc.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (doc.payment_status === "expired" || doc.payment_status === "canceled") {
          setStatus("failed");
          return;
        }
        attempts += 1;
        if (attempts < maxAttempts) {
          setTimeout(poll, 3000);
        } else {
          setStatus("pending");
        }
      } catch {
        setStatus("error");
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="max-w-2xl mx-auto px-4 py-16">
        <Card>
          <CardContent className="p-8 text-center">
            {status === "loading" && (
              <>
                <div className="text-5xl mb-4 animate-pulse">⏳</div>
                <h1 className="text-2xl font-bold mb-2">Verifica pagamento in corso...</h1>
                <p className="text-gray-600">Attendi qualche secondo.</p>
              </>
            )}
            {status === "paid" && (
              <>
                <div className="text-5xl mb-4">✅</div>
                <h1 className="text-2xl font-bold mb-2">Pagamento completato!</h1>
                <p className="text-gray-700 mb-4">
                  Grazie {data?.customer_name || ""}, il tuo pagamento di{" "}
                  <strong>€ {Number(data?.amount || 0).toFixed(2).replace(".", ",")}</strong> è stato ricevuto.
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  Causale: <em>{data?.causale}</em>
                </p>
                <p className="text-sm text-gray-500">Riceverai conferma via email a breve.</p>
              </>
            )}
            {status === "pending" && (
              <>
                <div className="text-5xl mb-4">⏱️</div>
                <h1 className="text-2xl font-bold mb-2">Pagamento in elaborazione</h1>
                <p className="text-gray-600">
                  Il pagamento è ancora in elaborazione. Riceverai una conferma via email non appena sarà confermato.
                </p>
              </>
            )}
            {status === "failed" && (
              <>
                <div className="text-5xl mb-4">❌</div>
                <h1 className="text-2xl font-bold mb-2">Pagamento non completato</h1>
                <p className="text-gray-600">Il pagamento è stato annullato o è scaduto. Chiedi un nuovo link al venditore.</p>
              </>
            )}
            {status === "error" && (
              <>
                <div className="text-5xl mb-4">⚠️</div>
                <h1 className="text-2xl font-bold mb-2">Errore di verifica</h1>
                <p className="text-gray-600">Non siamo riusciti a verificare lo stato del pagamento. Contatta il venditore.</p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

/**
 * /paga/annullato — Stripe cancel URL landing.
 */
export const CustomPaymentCancelledPage = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="max-w-2xl mx-auto px-4 py-16">
        <Card>
          <CardContent className="p-8 text-center">
            <div className="text-5xl mb-4">↩️</div>
            <h1 className="text-2xl font-bold mb-2">Pagamento annullato</h1>
            <p className="text-gray-600 mb-6">Hai annullato il pagamento. Puoi riprovare quando vuoi.</p>
            <Button onClick={() => navigate(-1)} data-testid="paga-cancel-back-btn">
              Torna indietro
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
